import { useState } from "react";
import { X, CreditCard, CheckCircle, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";

const AfterpayBanner = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  if (!isVisible) return null;

  const text = {
    en: {
      headline: "Pay in 4 interest-free installments",
      subtext: "Available on ALL treatments • No credit check • Instant approval",
      cta: "Learn More",
      close: "Got it",
      detailsTitle: "How Afterpay Works",
      steps: [
        { title: "Choose Afterpay", desc: "Select Afterpay at checkout" },
        { title: "Pay in 4", desc: "Split your purchase into 4 payments" },
        { title: "No Interest", desc: "Always interest-free when you pay on time" },
        { title: "Instant Use", desc: "Use immediately, pay over 6 weeks" },
      ],
      available: "Available on treatments from $35 - $2,000",
      signUp: "New to Afterpay? Sign up in minutes!",
    },
    fr: {
      headline: "Payez en 4 versements sans intérêt",
      subtext: "Disponible sur TOUS les traitements • Pas de vérification de crédit • Approbation instantanée",
      cta: "En savoir plus",
      close: "Compris",
      detailsTitle: "Comment fonctionne Afterpay",
      steps: [
        { title: "Choisissez Afterpay", desc: "Sélectionnez Afterpay à la caisse" },
        { title: "Payez en 4", desc: "Divisez votre achat en 4 paiements" },
        { title: "Sans Intérêt", desc: "Toujours sans intérêt si payé à temps" },
        { title: "Utilisation Immédiate", desc: "Utilisez immédiatement, payez sur 6 semaines" },
      ],
      available: "Disponible sur les traitements de 35$ à 2 000$",
      signUp: "Nouveau sur Afterpay? Inscrivez-vous en quelques minutes!",
    },
  };

  const t = text[language] || text.en;
  // Short headline used on the narrow mobile bar — the long form ("Pay in 4
  // interest-free installments") clipped under the close (×) button on
  // iPhone-sized viewports and forced "Learn More" to wrap onto a 2nd line.
  const shortHeadline = language === "fr" ? "Payez en 4 · Sans intérêt" : "Pay in 4 · Interest-free";

  return (
    <>
      {/* Main Banner — hidden on mobile since the TopBar already rotates
          the Afterpay promo; showing both caused overlap with the fixed
          header plaque on iPhone-sized viewports. */}
      <div className="hidden md:block bg-black text-white py-2.5 px-4 relative overflow-hidden mt-0">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="bg-[#b2fce4] text-black text-sm font-bold px-3 py-1 rounded tracking-wide">
              afterpay
            </span>
            <span className="text-sm font-medium">{t.headline}</span>
          </div>
          <span className="hidden md:inline text-xs text-gray-400">•</span>
          <span className="hidden md:inline text-xs text-gray-300">{t.subtext}</span>
          <button
            onClick={() => setShowDetails(true)}
            className="text-[#b2fce4] text-xs font-semibold hover:underline flex items-center gap-1"
          >
            {t.cta} <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <button
          onClick={() => setIsVisible(false)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Details Modal */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowDetails(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="text-center mb-6">
                <span className="bg-black text-[#b2fce4] text-xl font-bold px-4 py-2 rounded inline-block mb-3">
                  afterpay
                </span>
                <h3 className="text-2xl font-bold text-gray-900">{t.detailsTitle}</h3>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {t.steps.map((step, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="w-10 h-10 bg-[#b2fce4] rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-black font-bold">{i + 1}</span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">{step.title}</h4>
                    <p className="text-xs text-gray-600">{step.desc}</p>
                  </div>
                ))}
              </div>

              <div className="bg-black rounded-xl p-4 text-center mb-4">
                <p className="text-[#b2fce4] font-semibold mb-1">{t.available}</p>
                <p className="text-white text-sm">{language === "fr" ? "Choisissez Afterpay au moment du paiement!" : "Just select Afterpay at checkout!"}</p>
              </div>

              <div className="flex gap-3">
                <a
                  href="/financing"
                  className="flex-1 bg-[#b2fce4] text-black font-bold py-3 rounded-lg text-center hover:bg-[#9eecd4] transition-colors no-underline"
                >
                  {language === "fr" ? "En savoir plus" : "Learn More"}
                </a>
                <button
                  onClick={() => setShowDetails(false)}
                  className="flex-1 bg-gray-100 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t.close}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Floating Afterpay Badge Component
export const FloatingAfterpayBadge = () => {
  const { language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="fixed right-4 top-[40%] z-40 hidden lg:block">
      <motion.div
        className="relative"
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        <motion.div
          animate={{ 
            width: isExpanded ? "auto" : "48px",
          }}
          className="bg-black rounded-full shadow-lg cursor-pointer overflow-hidden"
        >
          <div className="flex items-center gap-2 px-3 py-2 whitespace-nowrap">
            <span className="bg-[#b2fce4] text-black text-[10px] font-bold px-2 py-1 rounded">
              afterpay
            </span>
            <AnimatePresence>
              {isExpanded && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  className="text-white text-xs font-medium"
                >
                  {language === "fr" ? "Payez en 4x" : "Pay in 4"}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

// Afterpay Info Section for Homepage
export const AfterpayInfoSection = ({ className = "" }) => {
  const { language } = useLanguage();

  const text = {
    en: {
      badge: "FINANCING AVAILABLE",
      headline: "Pay Later with",
      subhead: "Split any treatment into 4 interest-free payments",
      features: [
        { icon: "💳", title: "No Credit Check", desc: "Instant approval in minutes" },
        { icon: "✨", title: "Interest-Free", desc: "Always 0% when paid on time" },
        { icon: "🎯", title: "Use Immediately", desc: "Get your treatment today" },
        { icon: "📱", title: "Easy Sign Up", desc: "Quick registration on your phone" },
      ],
      example: "Example: $600 treatment = 4 payments of $150",
      cta: "Available on ALL services",
    },
    fr: {
      badge: "FINANCEMENT DISPONIBLE",
      headline: "Payez plus tard avec",
      subhead: "Divisez tout traitement en 4 paiements sans intérêt",
      features: [
        { icon: "💳", title: "Pas de Vérification", desc: "Approbation instantanée" },
        { icon: "✨", title: "Sans Intérêt", desc: "Toujours 0% si payé à temps" },
        { icon: "🎯", title: "Utilisez Maintenant", desc: "Recevez votre traitement aujourd'hui" },
        { icon: "📱", title: "Inscription Facile", desc: "Inscription rapide sur votre téléphone" },
      ],
      example: "Exemple: Traitement de 600$ = 4 paiements de 150$",
      cta: "Disponible sur TOUS les services",
    },
  };

  const t = text[language] || text.en;

  return (
    <section 
      id="afterpay-section"
      className={`pt-8 pb-16 relative ${className}`}
      style={{ 
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0d0d0d 50%, #1a1a2e 100%)',
        isolation: 'isolate',
        zIndex: 1,
        scrollMarginTop: '200px'
      }}
    >
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="text-center mb-10">
          <span className="inline-block bg-[#b2fce4]/20 text-[#b2fce4] text-xs font-bold px-4 py-1.5 rounded-full mb-4 tracking-wider">
            {t.badge}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t.headline}{" "}
            <span className="bg-[#b2fce4] text-black px-3 py-1 rounded ml-1">afterpay</span>
          </h2>
          <p className="text-gray-400 text-lg mt-3">{t.subhead}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {t.features.map((feature, i) => (
            <div key={i} className="bg-white/5 backdrop-blur-sm rounded-xl p-5 text-center border border-white/10 hover:border-[#b2fce4]/30 transition-colors">
              <span className="text-3xl mb-3 block">{feature.icon}</span>
              <h4 className="text-white font-semibold mb-1">{feature.title}</h4>
              <p className="text-gray-400 text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <div className="inline-block bg-[#b2fce4] text-black font-bold px-6 py-3 rounded-lg mb-3">
            {t.example}
          </div>
          <p className="text-[#b2fce4] font-semibold">{t.cta}</p>
        </div>
      </div>
    </section>
  );
};

export default AfterpayBanner;
